import React from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Modal, useModalModel } from '@workday/canvas-kit-react/modal';
import { SessionTimeout } from '@workday/upc-common';
import { useTranslation } from 'react-i18next';

export type IdleCheckProps = React.PropsWithChildren<{
    onIdle: () => void;
}>;

const ONE_HOUR = 60 * 60 * 1000;
const promptTimeout = 0.5 * ONE_HOUR; // How long the "do you want to stay logged in" message counts down (must be one hour or less)
const idleTimeout = 2 * ONE_HOUR; // How long a user can remain idle before the user is logged out

// this is a hack to enable testing
const timers = process.env.NODE_ENV === 'test' ? { timers: window } : {};

export function IdleCheck({ children, onIdle }: IdleCheckProps) {
    const { t } = useTranslation('shared');
    const modalModel = useModalModel({
        onHide: data => {
            // data will have a value if onHide was triggered by a DOM event
            if (data) {
                message({ type: 'closeModal' }, false);
            }
            checkAndReset();
        }
    });

    const { start, getRemainingTime, message, isIdle } = useIdleTimer({
        timeout: idleTimeout - promptTimeout,
        promptTimeout: promptTimeout,
        onPrompt: onPromptHandler,
        onIdle: onCloseModal,
        onMessage: onMessageHandler,
        crossTab: true,
        syncTimers: 250,
        ...timers
    });

    function onMessageHandler(message: any) {
        if (message.type === 'closeModal') {
            modalModel.events.hide();
        }
    }

    function onPromptHandler() {
        modalModel.events.show();
    }

    function onCloseModal(e?) {
        modalModel.events.hide(e);
    }

    function checkAndReset() {
        if (isIdle()) {
            onIdle();
        } else {
            start();
        }
    }

    return (
        <>
            {children}
            <Modal model={modalModel}>
                <Modal.Overlay>
                    <Modal.Card as='div'>
                        <Modal.CloseIcon aria-label='Close' as='button' onClick={onCloseModal} />
                        <Modal.Body as='div'>
                            <SessionTimeout
                                getRemainingTime={getRemainingTime}
                                onResetClick={onCloseModal}
                                textProps={{
                                    header: t('SessionTimeout.header'),
                                    description: t('SessionTimeout.description'),
                                    resetButtonText: t('SessionTimeout.resetButtonText')
                                }}
                            />
                        </Modal.Body>
                    </Modal.Card>
                </Modal.Overlay>
            </Modal>
        </>
    );
}
